import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Grid, List, SlidersHorizontal, Wrench } from 'lucide-react';
import { useTrucks } from '../hooks/useTrucks';
import { LoadingCard, LoadingPage } from '../components/LoadingSpinner';
import { SoldRibbon } from '../components/SoldRibbon';

const ChassisCabsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);

  const apiFilters = useMemo(() => {
    const filters = {};
    if (searchTerm) filters.search = searchTerm;
    if (selectedMake) filters.make = selectedMake;
    if (selectedYear) filters.year = parseInt(selectedYear);
    return filters;
  }, [searchTerm, selectedMake, selectedYear]);

  const { trucks: allTrucks, loading, error } = useTrucks(apiFilters);

  // Filter for chassis cabs and apply price range
  const chassisCabs = useMemo(() => {
    let filtered = allTrucks.filter(truck => 
      truck.bodytype && truck.bodytype.toLowerCase().includes('chassis')
    );

    if (priceRange) {
      filtered = filtered.filter(truck => {
        const price = truck.price || 0;
        switch (priceRange) {
          case 'under30k': return price < 30000;
          case '30k-40k': return price >= 30000 && price <= 40000;
          case '40k-50k': return price > 40000 && price <= 50000;
          case 'over50k': return price > 50000;
          default: return true;
        }
      });
    }

    return filtered;
  }, [allTrucks, priceRange]);

  const TruckCard = ({ truck, isListView = false }) => (
    <div className={`bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all group ${
      isListView ? 'flex' : ''
    }`}>
      <div className={`relative ${isListView ? 'w-1/3' : ''}`}>
        <img 
          src={truck.images[0]} 
          alt={truck.title}
          className={`object-cover group-hover:scale-105 transition-transform duration-300 ${
            isListView ? 'w-full h-full' : 'w-full h-48'
          } ${truck.is_sold ? 'grayscale opacity-80' : ''}`}
        />
        {truck.is_sold && <SoldRibbon size="md" />}
        {truck.available_for_hire && (
          <div className="absolute top-4 left-4">
            <div className="bg-green-600 text-white px-3 py-1 rounded text-sm font-bold shadow-lg">
              Available for Hire
            </div>
          </div>
        )}
      </div>
      <div className={`p-6 ${isListView ? 'flex-1' : ''}`}>
        <h3 className="text-xl font-bold text-black mb-2 font-inter line-clamp-2">{truck.title}</h3>
        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
          <span>{truck.year}</span>
          <span>•</span>
          <span>{truck.weight}t</span>
          <span>•</span>
          <span>{truck.power}</span>
        </div>
        <p className="text-gray-600 mb-4 line-clamp-2 font-inter">{truck.description}</p>
        
        <div className="flex justify-between items-center">
          <div className="text-2xl font-bold text-black">
            {truck.is_sold ? <span className="text-red-600">SOLD</span> : (truck.price ? `£${truck.price.toLocaleString()}` : 'POA')}
          </div>
          <Link 
            to={`/trucks/${truck.id}`}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded font-bold transition-colors"
          >
            View Details
          </Link>
        </div>
      </div>
    </div>
  );

  if (loading && chassisCabs.length === 0) {
    return <LoadingPage message="Loading chassis cabs..." />;
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-black text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-orange-600 rounded-full p-4">
                <Wrench className="h-12 w-12 text-white" />
              </div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-black font-inter mb-4">
              Chassis <span className="text-orange-500">Cabs</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-inter">
              Bare chassis ready for custom body fitting. Perfect for specialised applications and bespoke requirements.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Similar search and filter structure */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search chassis cabs by make, model, or specifications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
            />
          </div>
        </div>

        <div className="mb-6">
          <p className="text-gray-600 font-inter">
            Showing {chassisCabs.length} chassis cab{chassisCabs.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {chassisCabs.map((truck) => (
            <TruckCard key={truck.id} truck={truck} />
          ))}
        </div>

        {chassisCabs.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🔧</div>
            <h3 className="text-xl font-bold text-gray-700 mb-2 font-inter">No chassis cabs available</h3>
            <p className="text-gray-600 font-inter">We currently don't have chassis cabs in stock. <Link to="/contact" className="text-orange-600 hover:underline">Contact us</Link> for custom requirements and upcoming availability.</p>
          </div>
        )}

        {/* Custom Body Options */}
        <div className="mt-16 bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-3xl font-black text-black mb-6 font-inter text-center">
            Custom <span className="text-orange-600">Body Options</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 border border-gray-200 rounded-lg">
              <h3 className="font-bold text-black mb-2 font-inter">Tipper Bodies</h3>
              <p className="text-gray-600 font-inter">Hydraulic tipping systems for construction and waste management.</p>
            </div>
            <div className="text-center p-6 border border-gray-200 rounded-lg">
              <h3 className="font-bold text-black mb-2 font-inter">Crane Bodies</h3>
              <p className="text-gray-600 font-inter">Mounted crane systems for lifting and positioning operations.</p>
            </div>
            <div className="text-center p-6 border border-gray-200 rounded-lg">
              <h3 className="font-bold text-black mb-2 font-inter">Specialist Bodies</h3>
              <p className="text-gray-600 font-inter">Bespoke solutions for unique industry requirements.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChassisCabsPage;